import type {UUID} from '@gl/lit/utils/uuid.js';
import type {HttpServiceError} from '@gl/lit/lib/http_service/errors.js';
import type {Either} from './Either.js';
import type {RequestMethod} from '@gl/lit/common/constants/index.js';

export type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export enum RequestStatus {
  /**
   * Initial status. No request was ever done.
   */
  NOT_INITIATED = 'not-initiated',
  /**
   * Indicate that the request is still running.
   */
  FETCHING = 'fetching',
  /**
   * The request was interrupted before it could end.
   */
  ABORTED = 'aborted',
  /**
   * The request was successful, returning the data required (if the case)
   */
  SUCCESS = 'success',
  /**
   * Something went wrong, and the request did not returned what was expected.
   */
  FAIL = 'fail',
}

/**
 * The way it should handle duplicated requests.
 * Duplicated requests are identified by the requestKey or by the url
 * The options are:
 * 'allow-multiple' - multiple requests can stay open against the same endpoint
 * 'use-previous' - listen the currently pending request instead of opening a new request
 * 'abort-previous' - a new request will cancel any previous one that is still pending and open a new request
 */
export enum DuplicateRequestPolicy {
  /** multiple requests can stay open against the same endpoint */
  ALLOW_MULTIPLE = 'allow-multiple-requests',
  /** listen the currently pending request instead of opening a new request */
  USE_PREVIOUS = 'use-previous-request',
  /** it will open a new request and will cancel any previous one that is still fetching */
  ABORT_PREVIOUS = 'abort-previous-request',
}

/**
 * It represents default options to be used when making HTTP requests.
 * It contains properties such as the HTTP method, credentials, headers, and more.
 */
export type HTTPServiceDefaults = {
  method: HTTPMethod;
  credentials: RequestCredentials;
  headers: HeadersInit;
  mode: RequestMode;
  referrer: string;
  referrerPolicy: ReferrerPolicy;
  /**
   * Http service will retry failed requests by default.
   * This setting defines how many times it will retry.
   * 0 means no retry
   * @default 3
   */
  maxRetries: number;
  /**
   * The way duplicated requests will be handled
   * @see DuplicateRequestPolicy
   */
  duplicateRequestPolicy: DuplicateRequestPolicy;
  /**
   * Defines the maximum period of time (in milliseconds)
   * the client will wait for a response.
   * After it, the request will be aborted automatically.
   * @default 2500 milliseconds
   */
  timeout: number;
};

/**
 * This is an interface that extends the RequestInit interface provided by the fetch API.
 * It adds several custom properties, such as requestId, requestKey, maxRetries, and duplicateRequestPolicy,
 * which can be used to configure an HTTP request with specific options.
 */
export type RequestOptions = Omit<RequestInit, 'body' | 'method'> & {
  body?: Record<string, unknown> | FormData | string | number | boolean;
  method: RequestMethod | HTTPMethod;
  requestId?: UUID;
  requestKey?: string;
  maxRetries?: number;
  duplicateRequestPolicy?: DuplicateRequestPolicy;
  timeout?: number;
  responseParser?: 'json' | 'formdata' | 'text' | 'blob' | null;
};

export type HttpServiceResponse<TError, TSuccess> = Promise<
  Either<TError | HttpServiceError, TSuccess>
>;
